import { useCallback, useEffect, useRef, useState } from 'react';
import * as Speech from 'expo-speech';
import { synthetiserReplique, VoixIndisponible } from '../services/voixRecruteur';
import { jouerBase64, preparerLecture, type Lecture } from '../services/lecteurVoix';
import type { InterviewRole, OutputLanguage } from '../types';

/**
 * VOIX DU RECRUTEUR : ElevenLabs d'abord, voix du téléphone en repli — jamais de blocage.
 *
 * La synthèse part dès que la réplique arrive (l'appelant appelle `lire` à l'arrivée du texte).
 * Au-delà de `DELAI_MAX_VOIX_MS`, ou à la moindre erreur, la voix du téléphone lit la réplique et
 * l'audio ElevenLabs arrivé trop tard est jeté : deux voix qui se chevauchent seraient pires
 * qu'une voix robotique.
 */

/** Délai maximal entre l'arrivée du texte et l'audio prêt ; au-delà, la voix du téléphone lit. */
export const DELAI_MAX_VOIX_MS = 6000;

export type SourceVoix = 'elevenlabs' | 'telephone';

export interface MesureVoix {
  source: SourceVoix;
  /** Fin du texte → début de la voix, pour ElevenLabs. `null` pour la voix du téléphone. */
  delaiMs: number | null;
  raison?: 'sans_jeton' | 'plafond' | 'plafond_entretien' | 'indisponible' | 'delai' | 'lecture';
}

export function useVoixRecruteur(role: InterviewRole, langue: OutputLanguage, entretienId: string) {
  const lectureRef = useRef<Lecture | null>(null);
  // Chaque `lire` ou `arreter` invalide les réponses en vol : une réplique coupée ne démarre jamais.
  const sequenceRef = useRef(0);
  const plafondRef = useRef(false);
  const [mesure, setMesure] = useState<MesureVoix | null>(null);

  const arreter = useCallback(() => {
    sequenceRef.current += 1;
    lectureRef.current?.arreter();
    lectureRef.current = null;
    Speech.stop();
  }, []);

  const lire = useCallback(
    (texte: string, jeton?: string) => {
      arreter();
      const sequence = sequenceRef.current;
      // La voix du téléphone sort aussi sur le haut-parleur : la session est remise en lecture avant
      // de parler, et une réplique coupée entre-temps ne démarre pas.
      const telephone = (raison: NonNullable<MesureVoix['raison']>) => {
        setMesure({ source: 'telephone', delaiMs: null, raison });
        void preparerLecture()
          .catch(() => undefined)
          .then(() => {
            if (sequence === sequenceRef.current) Speech.speak(texte, { language: langue === 'en' ? 'en-US' : 'fr-FR' });
          });
      };

      if (!jeton) return telephone('sans_jeton');
      if (plafondRef.current) return telephone('plafond');

      const debut = Date.now();
      let bascule = false;
      const minuteur = setTimeout(() => {
        if (sequence !== sequenceRef.current) return;
        bascule = true;
        telephone('delai');
      }, DELAI_MAX_VOIX_MS);

      synthetiserReplique({ texte, jeton, role, langue, entretienId })
        .then(async (audio) => {
          clearTimeout(minuteur);
          if (bascule || sequence !== sequenceRef.current) return;
          try {
            const lecture = await jouerBase64(audio, () => {
              if (sequence === sequenceRef.current) setMesure({ source: 'elevenlabs', delaiMs: Date.now() - debut });
            });
            if (sequence !== sequenceRef.current) return lecture.arreter();
            lectureRef.current = lecture;
          } catch {
            if (sequence === sequenceRef.current) telephone('lecture');
          }
        })
        .catch((e: unknown) => {
          clearTimeout(minuteur);
          const raison = e instanceof VoixIndisponible ? e.raison : 'indisponible';
          if (raison === 'plafond' || raison === 'plafond_entretien') plafondRef.current = true;
          if (bascule || sequence !== sequenceRef.current) return;
          telephone(raison);
        });
    },
    [arreter, role, langue, entretienId],
  );

  useEffect(() => arreter, [arreter]);

  return { lire, arreter, mesure };
}
